/**
 * Application route enums.
 * Contains all authentication, portal, studio, and feasibility route paths.
 */

export enum AuthRoutes {
    SIGNIN = '/sign-in',
    SIGNOUT = '/sign-out',
    ACCOUNT_BILLING = '/account/billing',
    FORGOT_PASSWORD = '/forgot-password',
    RESET_PASSWORD = '/reset-password',
    RESEND_VERIFICATION = '/resend-verification',
    LEGACY_SIGN_UP = '/create/account',
    SIGNUP = '/sign-up',
    REGISTER = '/register',
    VERIFY_EMAIL = '/verify-email',
    EMAIL_VERIFIED = '/email-verified',
    ERROR_PAGE = '/error',
    SIGNUP_SUCCESS = '/signup-success',
}

export enum PortalRoutes {
    PORTAL = '/portal',
    PORTAL_PROJECT = '/portal/:projectId',
    PORTAL_PROJECT_FIELD = '/portal/:projectId/field',
    PORTAL_PROJECT_FIELD_QUESTIONS = '/portal/:projectId/field/questions',
    PORTAL_PROJECT_FIELD_MULTIMEDIA = '/portal/:projectId/analyze/multimedia',
    PORTAL_PROJECT_FIELD_INSIGHTS = '/portal/:projectId/analyze/insights',
    PORTAL_PROJECT_FIELD_DOCUMENTS = '/portal/:projectId/analyze/documents',
}

export enum StudioRoutes {
    HOME = '/home',
    BUILDER = '/builder',
    ACCOUNT = '/account',
    STUDIO_AI = '/dani',
    STUDIO_AI_HISTORY = '/dani/history',
    STUDIO_AI_REPORTS = '/dani/reports',
    STUDIO_AI_REPORT = '/dani/:conversationId/:reportId',
    STUDIO_AI_RELEASE_NOTES = '/dani/release-notes',
    // Sandbox namespace for routes authored in the /dani shell and synced into
    // production (e.g. /ai/studio-catalog). Gated like Studio AI routes.
    STUDIO_AI_SANDBOX = '/ai',
    // Public (tokenized) landing page for insight-email recipients.
    INSIGHTS_CONTINUE = '/insights/continue',
    MY_SURVEYS = '/projects/my-surveys',
    SURVEY_PULSE = '/projects/survey-pulse',
    FEASIBILITY = '/feasibility',
    MY_WORK = '/my-work',
    CROSSTABS = '/crosstab',
    SLIDE_CREATION = '/slide-creation',
    AI_CROSSTABS = '/ai/crosstabs/:surveyId',
    REPORTS = '/reports',
    ADMIN = '/tools',
}

/** Query flag that turns on Status's Multimedia checkbox. */
export const MULTIMEDIA_QUERY_PARAM = 'multimedia';

/** Videos (nav + Home) lands on Status with Multimedia already checked. */
export const MY_SURVEYS_WITH_MULTIMEDIA = `${StudioRoutes.MY_SURVEYS}?${MULTIMEDIA_QUERY_PARAM}=1`;

export function multimediaQueryIsOn(value: string | null): boolean {
    if (!value) return false;
    return value === '1' || value.toLowerCase() === 'true';
}

export enum AdminReports {
    USERS_REPORT = '/tools/reports/users',
    CLIENTS_REPORT = '/tools/reports/clients',
    LICENSES_REPORT = '/tools/reports/licenses',
    DASHBOARDS_REPORT = '/tools/reports/dashboards',
    STUDIO_AI_CHAT_HISTORY_REPORT = '/tools/reports/ai-chat-history',
    MARKED_MESSAGES_REPORT = '/tools/reports/marked-messages',
    RELEASE_NOTES_REPORT = '/tools/reports/release-notes',
}

export enum AdminOrderForms {
    DEPOSITS_FORM = '/tools/forms/deposit',
    LICENSES_FORM = '/tools/forms/license',
    NEW_VTRACKER_FORM = '/tools/forms/vtracker',
    GROUP_MANAGEMENT_FORM = '/tools/forms/group-management',
}

export enum AdminMetrics {
    STUDIO_AI_METRICS = '/tools/metrics/dani',
    // Feature adoption across Studio (docs/pdlc/feature-adoption.md). Internal staff only.
    FEATURE_ADOPTION = '/tools/adoption',
    TRIAL_MONITORING = '/tools/trial-monitoring',
    DANI_ACCESS = '/tools/dani-access',
    FREEMIUM_USAGE = '/tools/freemium-usage',
    DANI_CONFIG = '/tools/ai-configs',
    PLAN_TEMPLATES = '/tools/plan-templates',
    PLAN_TEMPLATE_STYLES = '/tools/plan-template-styles',
    BANNER_PLAN_TEMPLATES = '/tools/banner-plan-templates',
    CROSSTAB_SETUPS = '/tools/crosstab-setups',
    // M4S-5777 X88: Research's kept weighting target sets. Internal, under /tools.
    CROSSTAB_WEIGHT_TARGETS = '/tools/crosstab-weight-targets',
    /** M4S-5777 X105: export presets per client and study type. Internal, under /tools. */
    CROSSTAB_EXPORT_PRESETS = '/tools/crosstab-export-presets',
    /** M4S-5777 X135: an account's minimum group size for AI Crosstabs. */
    CROSSTAB_PRIVACY = '/tools/crosstab-privacy',
    /** M4S-5777 X102: an account's default display preferences for AI Crosstabs. */
    CROSSTAB_DISPLAY_DEFAULTS = '/tools/crosstab-display-defaults',
    FEATURE_FLAGS = '/tools/feature-flags',
    FEATURE_FLAGS_QA = '/tools/feature-flags/qa',
    COST_DASHBOARD = '/tools/cost-dashboard',
    CREDITS_FLEET = '/tools/credits',
    CREDITS_RATE_CARD = '/tools/credits/rate-card',
    CREDITS_MARGINS = '/tools/credits/margins',
    CREDITS_COGS = '/tools/credits/cogs',
    CREDITS_LEDGER_SHADOW = '/tools/credits/ledger-shadow',
    CREDITS_ORGS = '/tools/credits/orgs',
    DATA_HEALTH = '/tools/data-health',
    DATA_CATALOG_POPULATE = '/tools/data-catalog-populate',
    ENGAGEMENT_CONFIG = '/tools/engagement-config',
    // Capability K rate administration (M4S-5377). Safe under INTERNAL_ROUTES
    // whatever the F-0.2 namespace decision lands on: this surface is
    // internal-and-admin permanently (A40) and Capability O never reads it.
    FEASIBILITY_RATES = '/tools/feasibility-rates',
    // Does DANI build the request an analyst would? Internal, beside the rates.
    FEASIBILITY_BENCHMARK = '/tools/feasibility-benchmark',
    // Autopilot accuracy benchmark (M4A2A-497). Internal staff only, read-only.
    OE_CODING_BENCHMARK = '/tools/oe-coding-benchmark',
    // The developer portal's protocol, for staff (docs/pdlc/builder-api.md).
    // Behind `dev-portal`, off until a later release.
    DEV_PORTAL = '/tools/dev-portal',
    // Trial onboarding email links (M4S-5584). Internal-only and not gated on
    // a flag: `hubspot-email-nurture` gates sending, not reading, and this
    // page is most useful before sending is on.
    ONBOARDING_EMAILS = '/tools/onboarding-emails',
    // The operations half of the same feature (M4S-5566, W5/W7/W8/W9). A
    // SIBLING of /tools/trial-monitoring, never a tab inside it: that page is
    // CS's daily read path, 863 lines with no tests, and nothing here may put
    // it at risk. Ungated for the same reason as ONBOARDING_EMAILS above.
    TRIAL_ONBOARDING = '/tools/trial-onboarding',
    INSIGHT_ENGINE = '/dani/insight-engine',
    MINE_INSIGHTS_ENGINE = '/tools/discover-mine',
}

export enum InternalRoutes {
    FEASIBILITY = '/feasibility',
}

/**
 * Feasibility with DANI — reserved route prefixes (D3, decided 2026-09-08,
 * recorded on M4S-5362 / M4S-5371).
 *
 * Reserved, not yet built: the surfaces behind FEASIBILITY_INTERNAL land in
 * F-2/F-3/F-4 and FEASIBILITY_EXTERNAL in F-8 (week 4+). They are declared here
 * so the prefixes cannot be claimed by something else in the meantime, and so
 * week-4 work starts from the decided path rather than re-deriving it.
 *
 * Why the external prefix is NOT under /dani, /feasibility, /ai or /tools:
 * all four are internal-only. `/feasibility` and `/ai` and `/tools` sit in
 * INTERNAL_ROUTES (routeAccess.ts), which bounces any non-internal session, and
 * `/dani` carries its own StudioAI entitlement gate. A licensed external session
 * can reach any path outside INTERNAL_ROUTES, so `/dani-feasibility` needs no
 * routeAccess change at all — `matchesRoutePrefix` matches on exact equality or
 * `startsWith(route + '/')`, so a hyphenated sibling of `/feasibility` is not
 * caught by it.
 *
 * CAUTION: `/dani-feasibility` is DANI-branded but is NOT under `/dani`, so it
 * does NOT inherit DANI's entitlement gate — deliberately. Capability O requires
 * only a licensed external session; only A69's natural-language path also
 * requires org.hasClientStudioAIAccess. Do not "fix" this by nesting it under
 * /dani, and do not treat the prefix or the feature flag as the authorization
 * boundary — that is F-8.2's audience gate.
 */
export enum FeasibilityDaniRoutes {
    /** Internal engine surfaces (F-2/F-3/F-4). Internal-only via /ai. */
    FEASIBILITY_INTERNAL = '/ai/feasibility',
    /** Customer self-serve, survey-only (F-8 / Capability O). Licensed external. */
    FEASIBILITY_EXTERNAL = '/dani-feasibility',
}

export enum FeasibilityRoutes {
    MAP = '/',
    CLIENT_DETAILS = '/client-details',
    PROFILE_QUOTAS = '/profile-quotas',
    SURVEY_DETAILS = '/survey-details',
    SUMMARY = '/summary',
    RELEASE_GROUP = '/release-groups',
}

// Utility function for feasibility route creation
export const createFeasibilityRoute = (
    id?: string,
    page?: keyof typeof FeasibilityRoutes,
): string => {
    if (!id) return InternalRoutes.FEASIBILITY;
    if (!page) return `${InternalRoutes.FEASIBILITY}/${id}`;
    return `${InternalRoutes.FEASIBILITY}/${id}/${FeasibilityRoutes[page]}`;
};

// Unified export for all routes
export const Routes = {
    ...AuthRoutes,
    ...PortalRoutes,
    ...StudioRoutes,
    ...InternalRoutes,
    ...FeasibilityRoutes,
    ...AdminReports,
    ...AdminOrderForms,
    ...AdminMetrics,
    MY_SURVEYS_WITH_MULTIMEDIA,
    MULTIMEDIA_QUERY_PARAM,
    createFeasibilityRoute,
};
